import { FC, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  Inventory as InventoryIcon,
} from "@mui/icons-material";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import MainTitle from "../../components/mainTitle";
import { CONSTANTS } from "../../../utils/CONSTS";
import { logger, pages } from "../../../utils/common";
import {
  useArchiveLessonPlanHook,
  useDeleteInventoryLinkHook,
  useDeleteLessonPlanHook,
  useDeleteSessionHook,
  useDeleteUnitHook,
  useLessonPlanByIdHook,
} from "../../../hooks/useLessonPlanHook";
import { useAuthUserState } from "../../../context/authUser.context";
import {
  canManageLessonPlans,
  formatTerm,
  renderLessonPlanStatusChip,
} from "./lessonPlan.helpers";
import { LessonPlanDetailViewProps } from "./lessonPlan.interfaces";
import {
  TLessonPlanInventoryLink,
  TLessonPlanSession,
  TLessonPlanStatus,
  TLessonPlanUnit,
} from "../../../apis/lessonPlan/lessonPlan.interfaces";
import ManageLessonPlanDialog from "./manageLessonPlanDialog";
import ManageUnitDialog from "./manageUnitDialog";
import ManageSessionDialog from "./manageSessionDialog";
import ManageInventoryLinkDialog from "./manageInventoryLinkDialog";

const MetaItem: FC<{ label: string; value?: string | number | null }> = ({
  label,
  value,
}) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1">
      {value === undefined || value === null || value === "" ? "—" : value}
    </Typography>
  </Grid>
);

type PendingDelete =
  | { kind: "lessonPlan" }
  | { kind: "unit"; unit: TLessonPlanUnit }
  | { kind: "session"; unitId: string; session: TLessonPlanSession }
  | { kind: "inventoryLink"; link: TLessonPlanInventoryLink }
  | null;

const LessonPlanDetailView: FC<LessonPlanDetailViewProps> = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: plan, isLoading, error } = useLessonPlanByIdHook(id);
  const { userDetails } = useAuthUserState();
  const archiveMutation = useArchiveLessonPlanHook();
  const deletePlanMutation = useDeleteLessonPlanHook();
  const deleteUnitMutation = useDeleteUnitHook();
  const deleteSessionMutation = useDeleteSessionHook();
  const deleteInventoryLinkMutation = useDeleteInventoryLinkHook();

  const [editPlanOpen, setEditPlanOpen] = useState(false);
  const [confirmArchiveOpen, setConfirmArchiveOpen] = useState(false);

  // Unit + session + inventory dialogs.
  const [unitDialog, setUnitDialog] = useState<{
    open: boolean;
    unit: TLessonPlanUnit | null;
  }>({ open: false, unit: null });
  const [sessionDialog, setSessionDialog] = useState<{
    open: boolean;
    unitId: string | null;
    session: TLessonPlanSession | null;
  }>({ open: false, unitId: null, session: null });
  const [inventoryDialog, setInventoryDialog] = useState<{
    open: boolean;
    link: TLessonPlanInventoryLink | null;
  }>({ open: false, link: null });

  const [pendingDelete, setPendingDelete] = useState<PendingDelete>(null);

  const goBack = () => navigate(pages.lessonPlan.path);
  const canManage = canManageLessonPlans(userDetails);

  const handleArchive = async () => {
    if (!plan) return;
    try {
      await archiveMutation.mutateAsync({ id: plan.id });
      logger.show_success("Lesson plan archived.");
    } catch (err) {
      logger.show_error(
        err instanceof Error ? err.message : "Failed to archive lesson plan.",
      );
    } finally {
      setConfirmArchiveOpen(false);
    }
  };

  const isDeletePending =
    deletePlanMutation.isPending ||
    deleteUnitMutation.isPending ||
    deleteSessionMutation.isPending ||
    deleteInventoryLinkMutation.isPending;

  const confirmDelete = async () => {
    if (!plan || !pendingDelete) return;
    try {
      switch (pendingDelete.kind) {
        case "lessonPlan":
          await deletePlanMutation.mutateAsync({ id: plan.id });
          logger.show_success("Lesson plan deleted.");
          setPendingDelete(null);
          navigate(pages.lessonPlan.path);
          return;
        case "unit":
          await deleteUnitMutation.mutateAsync({
            lessonPlanId: plan.id,
            unitId: pendingDelete.unit.id,
          });
          logger.show_success("Unit deleted.");
          break;
        case "session":
          await deleteSessionMutation.mutateAsync({
            lessonPlanId: plan.id,
            unitId: pendingDelete.unitId,
            sessionId: pendingDelete.session.id,
          });
          logger.show_success("Session deleted.");
          break;
        case "inventoryLink":
          await deleteInventoryLinkMutation.mutateAsync({
            lessonPlanId: plan.id,
            inventoryLinkId: pendingDelete.link.id,
          });
          logger.show_success("Inventory link removed.");
          break;
      }
      setPendingDelete(null);
    } catch (err) {
      logger.show_error(
        err instanceof Error ? err.message : "Failed to delete.",
      );
    }
  };

  const deletePromptCopy = (() => {
    if (!pendingDelete) return { title: "", body: "" };
    switch (pendingDelete.kind) {
      case "lessonPlan":
        return {
          title: "Delete lesson plan?",
          body: `Delete "${plan?.title}"? This soft-deletes the plan and removes it from all lists. This cannot be undone from the UI.`,
        };
      case "unit":
        return {
          title: "Delete unit?",
          body: `Delete unit "${pendingDelete.unit.title}"? All sessions inside this unit will also be deleted.`,
        };
      case "session":
        return {
          title: "Delete session?",
          body: `Delete session "${pendingDelete.session.title}"? This cannot be undone.`,
        };
      case "inventoryLink":
        return {
          title: "Remove inventory link?",
          body: `Remove "${pendingDelete.link.inventory?.name ?? "this inventory item"}" from this lesson plan?`,
        };
    }
  })();

  if (isLoading) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.lessonPlan} />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          data-testid="lesson-plan-detail-loading"
        >
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error || !plan) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.lessonPlan} />
        <Box p={3}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={goBack}
            data-testid="lesson-plan-detail-back"
          >
            Back to Lesson Plans
          </Button>
          <Alert severity="error" sx={{ mt: 2 }}>
            Failed to load lesson plan.
          </Alert>
        </Box>
      </>
    );
  }

  const units = plan.units ?? [];
  const inventory = plan.inventory ?? [];
  const courseLabel = plan.course?.name
    ? `${plan.course.name}${plan.course.code ? ` (${plan.course.code})` : ""}`
    : undefined;
  const isArchived = plan.status === "archived";

  return (
    <>
      <DocumentTitle title={CONSTANTS.title.lessonPlan} />
      <Grid container spacing={3} data-testid="lesson-plan-detail-view">
        <Grid item xs={12}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={goBack}
                data-testid="lesson-plan-detail-back"
              >
                Back
              </Button>
              <MainTitle title={plan.title} />
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              {renderLessonPlanStatusChip(plan.status as TLessonPlanStatus)}
              {canManage && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setEditPlanOpen(true)}
                    data-testid="edit-lesson-plan-button"
                  >
                    Edit
                  </Button>
                  {!isArchived && (
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<ArchiveIcon />}
                      onClick={() => setConfirmArchiveOpen(true)}
                      disabled={archiveMutation.isPending}
                      data-testid="archive-lesson-plan-button"
                    >
                      Archive
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setPendingDelete({ kind: "lessonPlan" })}
                    disabled={deletePlanMutation.isPending}
                    data-testid="delete-lesson-plan-button"
                  >
                    Delete
                  </Button>
                </>
              )}
            </Stack>
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <Card data-testid="lesson-plan-detail-meta">
            <CardContent>
              <Grid container spacing={2}>
                <MetaItem label="Academic Year" value={plan.academicYear} />
                <MetaItem label="Term" value={formatTerm(plan.term)} />
                <MetaItem label="Board" value={plan.board} />
                <MetaItem label="Class" value={plan.class?.name} />
                <MetaItem label="Course" value={courseLabel} />
                <MetaItem label="Teacher" value={plan.teacher?.name} />
                <MetaItem label="Subject" value={plan.subject} />
                <MetaItem label="Grade Level" value={plan.gradeLevel} />
                <MetaItem
                  label="Total Duration (min)"
                  value={plan.totalDuration}
                />
                <MetaItem
                  label="Curriculum Framework"
                  value={plan.curriculumFramework}
                />
                <MetaItem
                  label="Syllabus Reference"
                  value={plan.syllabusReference}
                />
                <MetaItem
                  label="Textbook Reference"
                  value={plan.textbookReference}
                />
                <MetaItem label="NCERT Chapter" value={plan.ncertChapter} />
                <MetaItem label="Version" value={plan.version} />
                <MetaItem
                  label="Created"
                  value={new Date(plan.createdAt).toLocaleString()}
                />
                <MetaItem
                  label="Updated"
                  value={new Date(plan.updatedAt).toLocaleString()}
                />
              </Grid>

              {plan.description && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Description
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {plan.description}
                  </Typography>
                </>
              )}

              {plan.tags && plan.tags.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Tags
                  </Typography>
                  <Box display="flex" gap={0.5} flexWrap="wrap">
                    {plan.tags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card data-testid="lesson-plan-detail-units">
            <CardContent>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Typography variant="h6">Units & Sessions</Typography>
                {canManage && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() =>
                      setUnitDialog({ open: true, unit: null })
                    }
                    data-testid="add-unit-button"
                  >
                    Add Unit
                  </Button>
                )}
              </Stack>
              {units.length === 0 ? (
                <Alert severity="info" data-testid="lesson-plan-no-units">
                  No units have been added to this lesson plan yet.
                </Alert>
              ) : (
                <List disablePadding>
                  {units.map((unit, idx) => (
                    <Box
                      key={unit.id}
                      sx={{ mb: 2 }}
                      data-testid={`lesson-plan-unit-${unit.id}`}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Box>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {unit.unitNumber}. {unit.title}
                          </Typography>
                          {unit.description && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 1 }}
                            >
                              {unit.description}
                            </Typography>
                          )}
                          {typeof unit.duration === "number" && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Duration: {unit.duration} min
                            </Typography>
                          )}
                        </Box>
                        {canManage && (
                          <Stack direction="row" spacing={0.5}>
                            <IconButton
                              size="small"
                              onClick={() =>
                                setUnitDialog({ open: true, unit })
                              }
                              data-testid={`edit-unit-${unit.id}`}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                setPendingDelete({ kind: "unit", unit })
                              }
                              data-testid={`delete-unit-${unit.id}`}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        )}
                      </Stack>

                      {unit.sessions && unit.sessions.length > 0 ? (
                        <List dense disablePadding sx={{ pl: 2 }}>
                          {unit.sessions.map((session) => (
                            <ListItem
                              key={session.id}
                              disableGutters
                              data-testid={`lesson-plan-session-${session.id}`}
                            >
                              <ListItemText
                                primary={`${session.sessionNumber}. ${session.title}`}
                                secondary={
                                  [
                                    typeof session.duration === "number"
                                      ? `${session.duration} min`
                                      : null,
                                    session.description ?? null,
                                  ]
                                    .filter(Boolean)
                                    .join(" · ") || undefined
                                }
                              />
                              {canManage && (
                                <ListItemSecondaryAction>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      setSessionDialog({
                                        open: true,
                                        unitId: unit.id,
                                        session,
                                      })
                                    }
                                    data-testid={`edit-session-${session.id}`}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() =>
                                      setPendingDelete({
                                        kind: "session",
                                        unitId: unit.id,
                                        session,
                                      })
                                    }
                                    data-testid={`delete-session-${session.id}`}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </ListItemSecondaryAction>
                              )}
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ pl: 2 }}
                        >
                          No sessions in this unit.
                        </Typography>
                      )}
                      {canManage && (
                        <Box sx={{ pl: 2, mt: 1 }}>
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() =>
                              setSessionDialog({
                                open: true,
                                unitId: unit.id,
                                session: null,
                              })
                            }
                            data-testid={`add-session-${unit.id}`}
                          >
                            Add Session
                          </Button>
                        </Box>
                      )}
                      {idx < units.length - 1 && <Divider sx={{ mt: 2 }} />}
                    </Box>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card data-testid="lesson-plan-detail-inventory">
            <CardContent>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <InventoryIcon color="action" />
                  <Typography variant="h6">Inventory</Typography>
                </Stack>
                {canManage && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() =>
                      setInventoryDialog({ open: true, link: null })
                    }
                    data-testid="add-inventory-link-button"
                  >
                    Link Item
                  </Button>
                )}
              </Stack>
              {inventory.length === 0 ? (
                <Alert
                  severity="info"
                  data-testid="lesson-plan-no-inventory"
                >
                  No inventory items linked to this lesson plan.
                </Alert>
              ) : (
                <List disablePadding>
                  {inventory.map((link) => (
                    <ListItem
                      key={link.id}
                      disableGutters
                      data-testid={`inventory-link-${link.id}`}
                    >
                      <ListItemText
                        primary={
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            <Typography variant="body1">
                              {link.inventory?.name ?? link.inventoryId}
                            </Typography>
                            {link.inventory?.type && (
                              <Chip
                                label={link.inventory.type}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Stack>
                        }
                        secondary={
                          [
                            typeof link.quantityRequired === "number"
                              ? `Needs: ${link.quantityRequired}`
                              : null,
                            typeof link.inventory?.quantity === "number"
                              ? `In stock: ${link.inventory.quantity}`
                              : null,
                            link.notes ?? null,
                          ]
                            .filter(Boolean)
                            .join(" · ") || undefined
                        }
                      />
                      {canManage && (
                        <ListItemSecondaryAction>
                          <IconButton
                            size="small"
                            onClick={() =>
                              setInventoryDialog({ open: true, link })
                            }
                            data-testid={`edit-inventory-link-${link.id}`}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              setPendingDelete({
                                kind: "inventoryLink",
                                link,
                              })
                            }
                            data-testid={`delete-inventory-link-${link.id}`}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      )}
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <ManageLessonPlanDialog
        open={editPlanOpen}
        onClose={() => setEditPlanOpen(false)}
        plan={plan}
      />

      <ManageUnitDialog
        open={unitDialog.open}
        onClose={() => setUnitDialog({ open: false, unit: null })}
        lessonPlanId={plan.id}
        unit={unitDialog.unit}
      />

      <ManageSessionDialog
        open={sessionDialog.open}
        onClose={() =>
          setSessionDialog({ open: false, unitId: null, session: null })
        }
        lessonPlanId={plan.id}
        unitId={sessionDialog.unitId ?? ""}
        session={sessionDialog.session}
      />

      <ManageInventoryLinkDialog
        open={inventoryDialog.open}
        onClose={() => setInventoryDialog({ open: false, link: null })}
        lessonPlanId={plan.id}
        link={inventoryDialog.link}
      />

      <Dialog
        open={confirmArchiveOpen}
        onClose={() =>
          archiveMutation.isPending ? undefined : setConfirmArchiveOpen(false)
        }
        data-testid="archive-lesson-plan-confirm"
      >
        <DialogTitle>Archive lesson plan?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Archive &ldquo;{plan.title}&rdquo;? Archived plans are hidden from
            the default list but remain accessible under the Archived filter.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmArchiveOpen(false)}
            disabled={archiveMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            color="warning"
            variant="contained"
            onClick={handleArchive}
            disabled={archiveMutation.isPending}
            startIcon={
              archiveMutation.isPending ? (
                <CircularProgress size={16} />
              ) : (
                <ArchiveIcon />
              )
            }
            data-testid="confirm-archive-lesson-plan-button"
          >
            Archive
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!pendingDelete}
        onClose={() => (isDeletePending ? undefined : setPendingDelete(null))}
        data-testid="lesson-plan-delete-confirm"
      >
        <DialogTitle>{deletePromptCopy.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{deletePromptCopy.body}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPendingDelete(null)}
            disabled={isDeletePending}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={isDeletePending}
            startIcon={
              isDeletePending ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon />
              )
            }
            data-testid="confirm-lesson-plan-delete-button"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LessonPlanDetailView;
